import { getCDNImageUrl } from "@/utils/Common";
import {
  ShieldTickIcon,
  Loading01Icon,
  OperationalBurdenIcon,
  ChartBreakoutSquareIcon,
  CheckCircleIcon,
  Loading02Icon,
  TransparencyIcon,
  FlexibilityIcon,
  ReliabilityIcon,
  ScalabilityIcon,
  Dataflow04Icon,
  Globe02Icon,
  DropIcon,
  UsersLeftIcon,
} from "@/public/assets";

// Hidden Costs Section
export const HIDDEN_COST_DATA = [
  {
    id: 1,
    icon: ShieldTickIcon,
    title: "Opacity in Pricing",
    description: "Hidden fees, zero visibility into costs",
  },
  {
    id: 2,
    icon: Loading01Icon,
    title: "Complex Supplier Networks",
    description: "Fragmented vendors, no central control",
  },
  {
    id: 3,
    icon: OperationalBurdenIcon,
    title: "Operational Burden",
    description: "Hours lost on low-value transactions",
  },
  {
    id: 4,
    icon: ChartBreakoutSquareIcon,
    title: "Supply Instability Risks",
    description: "Niche material delays disrupt your production",
  },
];

// Two Ways Approach Section
export const APPROACH_DATA = [
  {
    id: 1,
    title: "Current Supplier Optimization",
    description:
      "We analyze and negotiate within your existing supplier frameworks for immediate stability.",
    image: getCDNImageUrl("current-supplier-optimization.webp"),
    bulletIcon: CheckCircleIcon,
    bullets: [
      "Price benchmarking against global market pricing",
      "Cost avoidance (no new supplier qualification)",
      "Immediate stability",
    ],
  },
  {
    id: 2,
    title: "New Supplier Integration",
    description:
      "We strategically pivot to favourable supply positions under your direction, onboarding vetted new suppliers to unlock better pricing.",
    image: getCDNImageUrl("new-supplier-integration.webp"),
    bulletIcon: Loading02Icon,
    bullets: [
      "Extended supply terms (up to 3 years)",
      "Long-term price security",
      "Total cost improvement via reduced pricing, offsetting qualification costs",
    ],
  },
];

// Why Partner Section
export const WHY_PARTNER_DATA = [
  {
    id: 1,
    icon: TransparencyIcon,
    title: "Transparency",
    description:
      "Clear pricing with no hidden costs - giving you full confidence in every tail spend transaction from day one.",
  },
  {
    id: 2,
    icon: FlexibilityIcon,
    title: "Flexibility",
    description:
      "Adapts to your procurement needs dynamically, whether you're scaling up operations or optimising existing tail spend categories.",
  },
  {
    id: 3,
    icon: ReliabilityIcon,
    title: "Reliability",
    description:
      "Consistent supply with dedicated agents ensures your tail spend supply chain never becomes a production bottleneck.",
  },
  {
    id: 4,
    icon: ScalabilityIcon,
    title: "Scalability",
    description:
      "Grow your procurement reach without growing your team. Our model scales with your business effortlessly.",
  },
];

// Elchemy Advantage Section
export const ADVANTAGE_DATA = [
  {
    id: 1,
    icon: Dataflow04Icon,
    title: "Tech-Driven Team",
    description: "AI benchmarking for accurate tail spend analysis",
  },
  {
    id: 2,
    icon: Globe02Icon,
    title: "Global Footprint",
    description: "Exports to 50-plus ports worldwide",
  },
  {
    id: 3,
    icon: DropIcon,
    title: "Specialty Expertise",
    description: "80% portfolio in high-value specialty chemicals",
  },
  {
    id: 4,
    icon: UsersLeftIcon,
    title: "Dedicated KAM",
    description: "One contact, exceptional service turnarounds",
  },
];

// FAQ Data
export const TAIL_SPEND_FAQ_DATA = [
  {
    question:
      "What is tail spend, and why is it a challenge for businesses?",
    answer:
      "Tail spend refers to low-value, high-volume transactions that fall outside a company's core procurement strategy. These purchases create real problems: opaque pricing, fragmented vendor bases, heavy operational burden, and supply instability risks that can interrupt production at the worst possible time. Without a proper tail spend management framework, these costs quietly compound.",
  },
  {
    question:
      "What specific benefits does Elchemy's Tail End Sourcing Support Program provide?",
    answer:
      "Our tail spend management program delivers a 30% reduction in supply risk through dedicated procurement experts who keep your operations running without interruption. Clients typically see 15% cost savings through consolidated spend and smarter logistics. Operational efficiency improves by 20% or more when you have a single point of contact handling delivery and documentation.",
  },
  {
    question:
      "How does Elchemy optimize my existing supplier relationships?",
    answer:
      "We start by working within your current supplier framework, using price benchmarking against live global market rates to find immediate savings without new qualification costs. Our goal with every tail spend analysis is to move unmanaged purchases into a fully optimized zone — with high visibility and measurable cost control that your team can track in real time.",
  },
  {
    question:
      "Can Elchemy help me transition to new, more favorable supply positions?",
    answer:
      "Yes. Under your direction, we can move you toward strategic suppliers who offer better long-term price and supply security. We negotiate fixed or reduced pricing terms, often for up to three years, so your tailspend management stays protected against market swings. Any initial qualification costs are designed to be offset by the negotiated advantage.",
  },
  {
    question:
      "How does our Elixir digital platform improve my supply chain visibility?",
    answer:
      "Elixir is Elchemy's proprietary digital procurement platform built to give you complete control over your tail spend supply chain. It offers single-click access to all shipment milestones and documents in one place, including live tracking, QC reports, and loading photographs in real time. AI-powered insights help flag pricing anomalies and supply risks before they become problems. Compliance is simplified too, with SDS forms and required regulatory information delivered in your preferred format, making managing tail spend operationally straightforward across every order.",
  },
  {
    question:
      "Which countries does Elchemy currently service for this program?",
    answer:
      "Elchemy's tail spend management program is built for global reach. We have established operations across North America, with stocking locations and logistics infrastructure already in place. We also actively serve customers across APAC, the EU, the Middle East, and South America through our network of vetted partners and port connections in over 50 locations worldwide. Wherever your supply chain operates, Elchemy has the capability to support it.",
  },
  {
    question:
      "What are Elchemy's capabilities for North American operations and JIT delivery?",
    answer:
      "We maintain stocking locations in New Jersey, Texas, and Illinois, to support localized fulfillment. Our Just-In-Time processing reduces on-site inventory complexity, and our logistics team handles door-to-door DDP shipments across the US and Canada - keeping your tail spend supply chain lean and predictable at every step.",
  },
  {
    question:
      "How does Elchemy ensure quality and regulatory compliance?",
    answer:
      "We are ISO 9001:2015, AEO, and FDA compliant. Regular supplier audits and strict QC protocols are standard across every order. We also have dedicated expertise in handling hazardous materials across IMDG Classes 1 through 9 - including flammable, toxic, and corrosive substances - making Elchemy a trusted partner for the most complex tail spend analysis and sourcing engagements.",
  },
];
